import React, { useEffect } from 'react';
import Loadable from 'react-loadable';
import { Switch } from 'react-router-dom';

import { Box } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import Entities from 'app/entities';
import Activate from 'app/modules/account/activate/activate';
import PasswordResetFinish from 'app/modules/account/password-reset/finish/password-reset-finish';
import PasswordResetInit from 'app/modules/account/password-reset/init/password-reset-init';
import Register from 'app/modules/account/register/register';
import { Home } from 'app/modules/home/home';
import Login from 'app/modules/login/login';
import Logout from 'app/modules/login/logout';
import PrivateRoute from 'app/shared/auth/private-route';
import ErrorBoundaryRoute from 'app/shared/error/error-boundary-route';
import PageNotFound from 'app/shared/error/page-not-found';
import { IntegrationDocs } from './modules/integration/IntegrationDocs';
import LiveMonitor from './modules/camera/LiveMonitor';
import CameraPlayback from './modules/camera/CameraPlayback';
import { useAppDispatch, useAppSelector } from './config/store';
import { getLoggedCompany } from './entities/company/company.reducer';

const allowedCompanyIds = ['280189051', '297090405', '66854360', '7880657109', '733878903', '10458010288', '7', '5390290883'];

export const verifyCanAccessClassification = () => {
  const companyEntity = useAppSelector(state => state.company.entity);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getLoggedCompany());
  }, []);
  const canAccessAssetClassification = companyEntity && allowedCompanyIds.includes(String(companyEntity.id));
  return canAccessAssetClassification;
};

const Account = Loadable({
  loader: () => import(/* webpackChunkName: "account" */ 'app/modules/account'),
  loading: () => <div>loading ...</div>,
});

const Admin = Loadable({
  loader: () => import(/* webpackChunkName: "administration" */ 'app/modules/administration'),
  loading: () => <div>loading ...</div>,
});

const Dashboards = Loadable({
  loader: () => import('app/modules/dashboards'),
  loading: () => <div>loading ...</div>,
});

const MaintenanceVehicleCustom = Loadable({
  loader: () => import('app/modules/maintenance-vehicle-custom'),
  loading: () => <div>loading ...</div>,
});

const TireCustom = Loadable({
  loader: () => import('app/modules/tire-custom'),
  loading: () => <div>loading ...</div>,
});

const LogisticRoutes = Loadable({
  loader: () => import('app/modules/logistic-routes'),
  loading: () => <div>loading ...</div>,
});
const LogisticRouteMapModule = Loadable({
  loader: () => import('app/modules/logistic-route-map'),
  loading: () => <div>loading ...</div>,
});

const Indicators = Loadable({
  loader: () => import('app/modules/indicators'),
  loading: () => <div>loading ...</div>,
});

const Reports = Loadable({
  loader: () => import('app/modules/reports'),
  loading: () => <div>loading ...</div>,
});

const AssetClassification = Loadable({
  loader: () => import('app/modules/asset-classification'),
  loading: () => <div>loading ...</div>,
});

const DriverClassification = Loadable({
  loader: () => import('app/modules/driver-classification'),
  loading: () => <div>loading ...</div>,
});

const StyledRoutes = () => {
  return (
    <Box className="box-container" style={{ height: '100%' }}>
      <Switch>
        <PrivateRoute path="/account/register" component={Register} hasAnyAuthorities={[AUTHORITIES.ADMIN]} />
        <PrivateRoute
          path="/admin"
          component={Admin}
          hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.MANUFACTURER_ADMIN, AUTHORITIES.REPRESENTATIVE_ADMIN, AUTHORITIES.TRANSPORTER_ADMIN]}
        />
        <PrivateRoute path="/account" component={Account} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.USER]} />
        {/* {verifyCanAccessClassification() && ( */}
        <PrivateRoute
          path="/asset-classification"
          component={AssetClassification}
          hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.MANUFACTURER_ADMIN, AUTHORITIES.REPRESENTATIVE_ADMIN, AUTHORITIES.TRANSPORTER_ADMIN]}
        />
        {/* )} */}
        <PrivateRoute
          path="/driver-classification"
          component={DriverClassification}
          hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.MANUFACTURER_ADMIN, AUTHORITIES.REPRESENTATIVE_ADMIN, AUTHORITIES.TRANSPORTER_ADMIN]}
        />
        {/* TODO: add AUTHORITIES to who can access the page */}
        <PrivateRoute path="/dashboards" component={Dashboards} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.USER]} />
        <PrivateRoute path="/indicators" component={Indicators} />
        <PrivateRoute path="/reports" component={Reports} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.USER]} />
        <PrivateRoute path="/maintenance-vehicle-custom" component={MaintenanceVehicleCustom} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.USER]} />
        <PrivateRoute path="/tire-custom" component={TireCustom} />
        <PrivateRoute path="/logistic-routes" component={LogisticRoutes} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.USER]} />
        <PrivateRoute path="/logistic-route-map" component={LogisticRouteMapModule} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.USER]} />
        <PrivateRoute path="/live-monitor" component={LiveMonitor} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.TRANSPORTER_ADMIN]} />
        <PrivateRoute path="/camera-playback" component={CameraPlayback} hasAnyAuthorities={[AUTHORITIES.ADMIN, AUTHORITIES.TRANSPORTER_ADMIN]} />
        <PrivateRoute path="/" component={Entities} hasAnyAuthorities={[AUTHORITIES.USER]} />
        <ErrorBoundaryRoute component={PageNotFound} />
      </Switch>
    </Box>
  );
};

const Routes = ({ isAuthenticated }) => {
  return (
    <Box className="view-routes">
      <Switch>
        <ErrorBoundaryRoute path="/login" component={Login} />
        <ErrorBoundaryRoute path="/logout" component={Logout} />
        <ErrorBoundaryRoute path="/" exact component={Home} />
        <ErrorBoundaryRoute path="/account/reset/request" component={PasswordResetInit} />
        <ErrorBoundaryRoute path="/account/activate/:key?" component={Activate} />
        <ErrorBoundaryRoute path="/account/reset/finish/:key?" component={PasswordResetFinish} />
        <ErrorBoundaryRoute path="/integration/docs" component={IntegrationDocs} isAuthenticated={isAuthenticated} />
        <StyledRoutes />
      </Switch>
    </Box>
  );
};

export default Routes;
